import React, { useCallback } from "react";
import { useAPI } from "../../../providers/ApiProvider";
import { unique } from "../../../utils/helpers";
import { importFiles } from "./utils";
import { importSources as extensionSources } from "@afriannotate/extensions";

const DEFAULT_COLUMN = "$undefined$";

export const useImportPage = (project, sample, initialImport) => {
  const [uploading, setUploadingStatus] = React.useState(false);
  const [fileIds, setFileIds] = React.useState([]);
  const [_columns, _setColumns] = React.useState([]);
  const addColumns = (cols) => _setColumns((current) => unique(current.concat(cols)));
  // undefined - no csv added, all good, keep moving
  // choose - csv added, block modal until user chooses a way to hangle csv
  // tasks | ts — choice made, all good, this cannot be undone
  const [csvHandling, setCsvHandling] = React.useState(); // undefined | choose | tasks | ts
  // Gold / honeypot import: when on, the committed tasks are seeded with a
  // ground-truth answer. ``answerColumn`` names the CSV/JSON column holding
  // the correct Choices value (server resolves the control).
  const [goldMode, setGoldMode] = React.useState(false);
  const [answerColumn, setAnswerColumn] = React.useState("answer");
  // Per-file CSV/TSV column mapping from the wizard's mapping modal:
  // {file_upload_id: {has_header: bool, columns: {source: target}}}.
  const [csvMappings, setCsvMappings] = React.useState({});
  // Why the import cannot be saved as it stands ("" when it can): a file
  // of a kind the labeling setup cannot show, a task file lacking a field.
  const [blocked, setBlocked] = React.useState("");
  // What each extension import source has been told to bring in, by source
  // id. Kept here so it outlives the tab being hidden, and so the wizard
  // can ask the source to finish when the project is saved.
  const [sourceState, setSourceStates] = React.useState(() =>
    initialImport?.source ? { [initialImport.source]: initialImport.state ?? null } : {},
  );
  const setSourceState = useCallback((id, state) => setSourceStates((all) => ({ ...all, [id]: state })), []);
  const uploadDisabled = csvHandling === "choose";
  const api = useAPI();

  // don't use columns from csv if we'll not use it as csv
  const columns = ["choose", "ts"].includes(csvHandling) ? [DEFAULT_COLUMN] : _columns;

  const finishUpload = async () => {
    setUploadingStatus(true);
    const imported = await api.callApi("reimportFiles", {
      params: {
        pk: project.id,
      },
      body: {
        file_upload_ids: fileIds,
        files_as_tasks_list: csvHandling === "tasks",
        // Gold/honeypot commit — seeds each task with a ground-truth answer.
        ...(goldMode ? { gold: true, answer_column: answerColumn || "answer" } : {}),
        // Column mapping chosen in the per-file modal (may be empty).
        ...(Object.keys(csvMappings).length ? { csv_mappings: csvMappings } : {}),
      },
    });

    setUploadingStatus(false);
    return imported;
  };

  // Let every extension source with something chosen do its part. Resolves
  // to "" when all went well, or to the first reason one could not finish.
  const finishSources = async () => {
    for (const source of extensionSources ?? []) {
      const state = sourceState[source.id];
      if (!source.finish || state === null || state === undefined) continue;
      setUploadingStatus(true);
      const result = await source.finish({ project, state, api }).catch((e) => ({ ok: false, message: String(e) }));
      setUploadingStatus(false);
      if (!result?.ok) return result?.message || "The data could not be brought in.";
    }
    return "";
  };

  const uploadSample = useCallback(
    async (sample, onStart, onFinish) => {
      onStart?.();
      const url = sample.url;
      const body = new URLSearchParams({ url });
      await importFiles({
        files: [{ name: url }],
        body,
        project,
      });
      onFinish?.();
    },
    [project],
  );

  const pageProps = {
    onWaiting: setUploadingStatus,
    // onDisableSubmit: onDisableSubmit,
    highlightCsvHandling: uploadDisabled,
    addColumns,
    csvHandling,
    setCsvHandling,
    onFileListUpdate: setFileIds,
    dontCommitToProject: true,
    // Gold/honeypot upload affordance state (rendered inside ImportPage).
    goldMode,
    setGoldMode,
    answerColumn,
    setAnswerColumn,
    csvMappings,
    setCsvMappings,
    onBlocking: setBlocked,
    initialSource: initialImport?.source,
    sourceState,
    setSourceState,
    // Raw, unmasked column list — the ``columns`` returned to the
    // wizard is masked to ``[DEFAULT_COLUMN]`` while the user is
    // still picking a CSV-handling mode, but for validation against
    // the label config we want what was actually parsed from the
    // uploaded data.
    uploadedColumns: _columns,
  };

  return { columns, uploading, uploadDisabled, finishUpload, finishSources, fileIds, pageProps, uploadSample, blocked };
};
